import React, { useEffect, useState } from 'react';
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { Switch, Modal, Input } from 'antd';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ListKeoTha.scss';
import { mixPostV2 } from '../../services/mixin';
import useToast from '../../hook/useToast';

export type CategoryColumnSourceColumn = {
  id: number;
  category_source_column_warehouse_id: number;
  source_colum_warehouse_id: number;
  name: string;
  key: string;
  is_show?: number;
  label?: string | null;
  position?: number | null;
};

export type SourceColumnWarehouseItem = {
  id: number;
  code: string;
  name: string;
  is_active: number;
  is_default: number;
  note: string;
  json: string[];
  category_column_source_column: CategoryColumnSourceColumn[];
  is_required: number;
};

export type Field = {
  id: string;
  title: string;
  keyName?: string;
  visible?: boolean;
  enabled?: boolean;
  is_required?: number;
  categoryColumnId?: number;
  is_show?: number;
  label?: string | null;
  position?: number | null;
};

export type Section = {
  id: string;
  title: string;
  fields: Field[];
};

export type ListKeoThaProps = {
  items?: SourceColumnWarehouseItem[];
};

const collator = new Intl.Collator('vi', { sensitivity: 'base', numeric: true });

function sortByTitleAZ<T extends { title: string }>(a: T, b: T): number {
  return collator.compare(a.title, b.title);
}

function sortByPosition(a: Field, b: Field): number {
  const pa = a.position ?? Number.MAX_SAFE_INTEGER;
  const pb = b.position ?? Number.MAX_SAFE_INTEGER;
  if (pa !== pb) return pa - pb;
  return collator.compare(a.title, b.title);
}

function buildSectionsFromItems(apiItems: SourceColumnWarehouseItem[]): Section[] {
  const sectionMap = new Map<string, { title: string; fields: Field[] }>();

  apiItems.forEach((item) => {
    item.category_column_source_column.forEach((cat: any) => {
      const sectionId = cat.category_source_column_warehouse_id.toString();
      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, { title: cat.name, fields: [] });
      }
      sectionMap.get(sectionId)!.fields.push({
        id: String(item.id),
        title: cat.label || item.name,
        keyName: item.code,
        visible: true,
        enabled: item.is_active === 1,
        is_required: item.is_required,
        categoryColumnId: cat.id,
        is_show: cat.is_show ?? 1,
        label: cat.label ?? null,
        position: cat.position ?? null,
      });
    });
  });

  const sections: Section[] = Array.from(sectionMap.entries()).map(([id, { title, fields }]) => ({
    id,
    title,
    fields: fields.sort(sortByPosition),
  }));

  return sections.sort(sortByTitleAZ);
}

/* Sortable field item */
function SortableField({
  field,
  sectionId,
  onShowEdit,
  onToggleEnabled,
  onClickFieldTitle,
  editable,
}: {
  field: Field;
  sectionId: string;
  onShowEdit: (data: any, type: any) => void;
  onToggleEnabled: (sectionId: string, fieldId: string, newValue: boolean) => void;
  onClickFieldTitle: (field: Field, sectionId: string) => void;
  editable: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${sectionId}-${field.categoryColumnId}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`pp-field ${isDragging ? 'pp-field-dragging' : ''} ${field.is_show === 0 ? 'pp-field-hidden' : ''}`}
    >
      <div className="pp-field-left">
        <div className="pp-grip" {...attributes} {...listeners}>
          <HolderOutlined />
        </div>
        <div className={`pp-field-title pp-field-title-clickable`} onClick={() => onClickFieldTitle(field, sectionId)}>
          {field.title} {field.is_required === 1 && <span className="text-danger">*</span>}
          {field.is_show === 0 && <span className="pp-field-hidden-tag">Ẩn</span>}
        </div>
      </div>

      <div className="pp-field-right">
        <div className="pp-icons">
          <div
            className="pp-eye w-24-8-D9D9D9"
            onClick={() => onShowEdit(field, 'view')}
          >
            <EyeOutlined style={{ opacity: field.visible ? 1 : 0.35 }} />
          </div>
          {editable && (
            <div className="pp-edit w-24-8-D9D9D9" onClick={() => onShowEdit(field, 'edit')}>
              <EditOutlined />
            </div>
          )}
        </div>
        {editable && (
          <div className="pp-switch">
            <Switch
              checked={!!field.enabled}
              className="rs-switch rs-switch"
              onChange={(checked) => onToggleEnabled(sectionId, field.id, checked)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ListKeoTha({ items = [], onShowEdit = (data: any, type: any) => {}, editable = false }: ListKeoThaProps & { onShowEdit?: (data: any, type: any) => void; editable?: boolean }) {
  const pushToast = useToast();
  const [sections, setSections] = useState<Section[]>([]);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  // Modal edit label & is_show
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<Field | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string>('');
  const [editLabel, setEditLabel] = useState('');
  const [editIsShow, setEditIsShow] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  const openEditModal = (field: Field, sectionId: string) => {
    setEditingField(field);
    setEditingSectionId(sectionId);
    setEditLabel(field.title || '');
    setEditIsShow((field.is_show ?? 1) === 1);
    setEditModalOpen(true);
  };

  const handleSaveFieldEdit = async () => {
    if (!editingField) return;
    setEditSaving(true);

    const sec = sections.find((s) => s.id === editingSectionId);
    if (!sec) { setEditSaving(false); return; }

    const fieldIndex = sec.fields.findIndex((f) => f.categoryColumnId === editingField.categoryColumnId);

    const updatedFields = sec.fields.map((f) =>
      f.categoryColumnId === editingField.categoryColumnId
        ? { ...f, title: editLabel || f.title, label: editLabel || null, is_show: editIsShow ? 1 : 0 }
        : f
    );

    // Optimistic update
    setSections((prev) =>
      prev.map((s) => (s.id === editingSectionId ? { ...s, fields: updatedFields } : s))
    );

    const data = [{
      id: editingField.categoryColumnId,
      position: editingField.position ?? (fieldIndex !== -1 ? fieldIndex + 1 : 1),
      is_show: editIsShow ? 1 : 0,
      label: editLabel || null,
    }];

    const res = await mixPostV2('/api/v1/be/update_category_column_multi', { data: JSON.stringify(data) });
    setEditSaving(false);

    if (res.status) {
      setEditModalOpen(false);
      pushToast('Cập nhật thành công!', 'success');
    } else {
      // Revert
      setSections((prev) =>
        prev.map((s) => (s.id === editingSectionId ? { ...s, fields: sec.fields } : s))
      );
      pushToast('Cập nhật thất bại!', 'warning');
    }
  };

  useEffect(() => {
    const freshSections = buildSectionsFromItems(items);
    setSections(freshSections);
    setOpenMap((prev) => {
      const m: Record<string, boolean> = { ...prev };
      freshSections.forEach((sec) => {
        if (!(sec.id in m)) m[sec.id] = false;
      });
      return m;
    });
  }, [items]);

  const toggleSection = (sectionId: string) => {
    setOpenMap((p) => ({ ...p, [sectionId]: !p[sectionId] }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragEnd = async (sectionId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const secIndex = sections.findIndex((s) => s.id === sectionId);
    if (secIndex === -1) return;

    const sec = sections[secIndex];
    const oldIndex = sec.fields.findIndex((f) => `${sectionId}-${f.categoryColumnId}` === active.id);
    const newIndex = sec.fields.findIndex((f) => `${sectionId}-${f.categoryColumnId}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sec.fields, oldIndex, newIndex);

    // Optimistic update
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, fields: reordered } : s))
    );

    // Build API payload
    const data = reordered.map((f, idx) => ({
      id: f.categoryColumnId,
      position: idx + 1,
      is_show: f.is_show ?? 1,
      label: f.label ?? null,
    }));

    const res = await mixPostV2('/api/v1/be/update_category_column_multi', { data: JSON.stringify(data) });
    if (!res.status) {
      // Revert on failure
      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, fields: sec.fields } : s))
      );
      pushToast('Cập nhật vị trí thất bại!', 'warning');
    }
  };

  const handleToggleEnabled = async (sectionId: string, fieldId: string, newValue: boolean) => {
    // Optimistic update
    setSections((prev) =>
      prev.map((sec) =>
        sec.id !== sectionId
          ? sec
          : {
              ...sec,
              fields: sec.fields.map((f) =>
                f.id === fieldId ? { ...f, enabled: newValue } : f
              ),
            }
      )
    );

    const res = await mixPostV2(`/api/v1/be/source_column_warehouse/${fieldId}`, {
      is_active: newValue ? 1 : 0,
      _method: 'PUT',
    });

    if (!res.status) {
      // Revert on failure
      setSections((prev) =>
        prev.map((sec) =>
          sec.id !== sectionId
            ? sec
            : {
                ...sec,
                fields: sec.fields.map((f) =>
                  f.id === fieldId ? { ...f, enabled: !newValue } : f
                ),
              }
        )
      );
      pushToast('Cập nhật trạng thái thất bại!', 'warning');
    }
  };

  return (
    <div className="pp-wrap-v2">
      {sections.map((sec) => {
        const open = !!openMap[sec.id];
        return (
          <div key={sec.id} className={`pp-section ${open ? 'pp-section-open' : ''}`}>
            <div className="pp-section-header" onClick={() => toggleSection(sec.id)}>
              <div className="pp-section-left">
                <div className="pp-section-arrow">{open ? <CaretDownOutlined /> : <CaretRightOutlined />}</div>
                <div className="pp-section-title">{sec.title}</div>
                <div className="pp-section-count">{sec.fields.length} params</div>
              </div>
              {editable && (
                <div className="pp-section-right" onClick={(e) => {
                  e.stopPropagation();
                  setOpenMap((p) => ({ ...p, [sec.id]: true }));
                  onShowEdit && onShowEdit({ sectionId: sec.id }, 'add')
                }}>
                  <PlusCircleOutlined />
                </div>
              )}
            </div>

            {open && (
              <div className="pp-section-content">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(sec.id, event)}
                >
                  <SortableContext
                    items={sec.fields.map((f) => `${sec.id}-${f.categoryColumnId}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="pp-fields-list">
                      {sec.fields.map((f) => (
                        <SortableField
                          key={`${sec.id}-${f.categoryColumnId}`}
                          field={f}
                          sectionId={sec.id}
                          onShowEdit={onShowEdit}
                          onToggleEnabled={handleToggleEnabled}
                          onClickFieldTitle={openEditModal}
                          editable={editable}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        );
      })}
      <Modal
        title="Chỉnh sửa param"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleSaveFieldEdit}
        confirmLoading={editSaving}
        okText="Lưu"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>Tên hiển thị</div>
          <Input
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            placeholder="Nhập tên hiển thị"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>Hiển thị</span>
          <Switch
            checked={editIsShow}
            onChange={(checked) => setEditIsShow(checked)}
            className="rs-switch rs-switch"
          />
        </div>
      </Modal>
    </div>
  );
}
